import { forwardRef, type HTMLAttributes } from "react";
import s from "./Screen.module.css";

export type ScreenProps = HTMLAttributes<HTMLDivElement>;

/**
 * Full-height screen wrapper. Handles the full-height + safe-area sizing math
 * (see Screen.module.css) so it isn't repeated per screen. Layout, padding,
 * and background stay in each screen's own CSS module — compose them onto the
 * same element via `className`, and reference the exposed
 * `--safe-top`/`--safe-right`/`--safe-bottom`/`--safe-left` custom properties
 * in that module's own padding calc.
 *
 * A shell that needs a hard `height` instead of this element's `min-height`
 * takes `height: 100%`, never a viewport unit: the percentage resolves
 * against the web view the app was actually given, which is the one thing
 * `vh`/`svh`/`dvh` can't be relied on to be in an installed home-screen app.
 * That requires the app's own `html`, `body` and `#root` to be `height: 100%`
 * — see each app's styles.css.
 *
 * Forwards its ref to the underlying element — the app shell uses this to
 * hand out its own root node as a `Sheet` portal container (see
 * `apps/client/src/app/AppShell.tsx`).
 */
export const Screen = forwardRef<HTMLDivElement, ScreenProps>(function Screen({ className, ...rest }, ref) {
  const classes = className ? `${s.root} ${className}` : s.root;
  return <div ref={ref} className={classes} {...rest} />;
});
